import { S3Client } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { XhrHttpHandler } from '@aws-sdk/xhr-http-handler';
import { useCallback } from 'react';

export function useUpload() {
  // we can't use expo-image-picker here because it does not handle large files well
  const pickFile = useCallback(async (type: 'video' | 'image') => {
    return new Promise<File | null>((resolve) => {
      // console.log('Starting file picker...');
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = type === 'video' ? 'video/*' : 'image/*';

      // Create a more reliable event listener for file selection
      const handleFileSelect = () => {
        // console.log('File selected:', input.files);
        const file = input.files?.[0];
        if (!file) {
          resolve(null);
          return;
        }

        // Remove event listeners to prevent memory leaks
        input.removeEventListener('change', handleFileSelect);
        document.removeEventListener('focus', handleFocusReturn);

        resolve(file);
      };

      // Safari sometimes fails to trigger change event when returning to the window
      // This focus event helps catch when the user returns to the page after selecting or canceling
      const handleFocusReturn = () => {
        // Small delay to ensure files property is updated
        setTimeout(() => {
          if (input.files && input.files.length > 0) {
            handleFileSelect();
          } else {
            // If no files after focus returns, assume it was canceled
            // console.log(
            //   'Upload appears to be canceled (focus returned without files)'
            // );
            document.removeEventListener('focus', handleFocusReturn);
            input.removeEventListener('change', handleFileSelect);
            resolve(null);
          }
        }, 300);
      };

      // Add both event listeners
      input.addEventListener('change', handleFileSelect);
      document.addEventListener('focus', handleFocusReturn);

      // Trigger file selection dialog
      input.click();
    });
  }, []);

  const getAssetInfo = useCallback(async (fileUri: File) => {
    return fileSizeAndType(fileUri);
  }, []);

  const startUpload = useCallback(
    async ({
      // this is a File, not a string - nomenclature is because native returns a string
      fileUri,
      presignedUrl,
      onProgress,
      onSuccess,
      onError
    }: {
      fileUri: File;
      presignedUrl: string;
      onProgress?: (bytesUploaded: number, bytesTotal: number) => void;
      onSuccess: () => void;
      onError: (error: any) => void;
    }) => {
      const url = new URL(presignedUrl);
      const region = url.searchParams.get('region');
      const bucket = url.searchParams.get('bucket');
      const accessKeyId = url.searchParams.get('accessKeyId');
      const secretAccessKey = url.searchParams.get('secretAccessKey');
      const sessionToken = url.searchParams.get('sessionToken');

      if (region && bucket && accessKeyId && secretAccessKey && sessionToken) {
        const credentials = {
          accessKeyId,
          secretAccessKey,
          sessionToken
        };

        return uploadLargeFile({
          key: url.pathname.slice(1),
          fileUri,
          region,
          bucket,
          credentials,
          onProgress,
          onSuccess,
          onError
        });
      }

      // small file uploading
      const xhr = new XMLHttpRequest();

      xhr.open('PUT', presignedUrl, true);

      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            onProgress(event.loaded, event.total);
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onSuccess();
        } else {
          onError(
            new Error(`Failed to upload file: ${xhr.status} ${xhr.statusText}`)
          );
        }
      };

      xhr.onerror = () => {
        onError(new Error('Network error occurred during upload'));
      };

      xhr.send(fileUri);
    },
    []
  );

  return { startUpload, getAssetInfo, pickFile };
}

async function uploadLargeFile({
  key,
  fileUri,
  region,
  bucket,
  credentials,
  onProgress,
  onSuccess,
  onError
}: {
  key: string;
  fileUri: File;
  region: string;
  bucket: string;
  credentials: {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken: string;
  };
  onProgress?: (bytesUploaded: number, bytesTotal: number) => void;
  onSuccess: () => void;
  onError: (error: any) => void;
}) {
  try {
    const client = new S3Client({
      // emits progress much more often: https://github.com/aws/aws-sdk-js-v3/tree/main/packages/xhr-http-handler
      requestHandler: new XhrHttpHandler({}),
      region,
      credentials
    });

    const parallelUploads3 = new Upload({
      client,
      params: {
        Bucket: bucket,
        Key: key,
        Body: fileUri
      },
      // (optional) concurrency configuration
      queueSize: 4,

      // (optional) size of each part, in bytes, at least 5MB
      partSize: 1024 * 1024 * 10,

      // (optional) when true, do not automatically call AbortMultipartUpload when
      // a multipart upload fails to complete. You should then manually handle
      // the leftover parts.
      leavePartsOnError: false
    });

    parallelUploads3.on('httpUploadProgress', (progress) => {
      if (onProgress && progress.loaded && progress.total) {
        onProgress(progress.loaded, progress.total);
      }
    });

    await parallelUploads3.done();

    onSuccess();
  } catch (error) {
    onError(error);
  }
}
function fileSizeAndType(file: File) {
  return {
    size: file.size,
    type: file.type
  };
}
